import { cn } from '@/utils/styles';
import { Circle, CircleAlert } from 'lucide-react';

type StepStatus = 'completed' | 'current' | 'upcoming' | 'error';

interface TimelineProgressBarProps {
  id: number;
  text: string;
  currentStepId: number;
  hasIssue: boolean;
  isLastStep: boolean;
  errorText?: string;
  'data-testid'?: string;
  subHeaderExplanation?: string;
}

export const TimelineProgressBar = ({
  id,
  text,
  currentStepId,
  hasIssue,
  isLastStep,
  errorText = '',
  'data-testid': testId,
  subHeaderExplanation,
}: TimelineProgressBarProps) => {
  const getStepStatus = (): StepStatus => {
    if (id < currentStepId) return 'completed';
    if (id === currentStepId) return hasIssue ? 'error' : 'current';
    return 'upcoming';
  };

  const status = getStepStatus();

  return (
    <div className="flex items-stretch gap-3" data-testid={testId}>
      <div className="flex flex-col items-center w-6 pt-1" data-testid={testId ? `${testId}-indicator` : undefined}>
        <div className="relative z-10 flex items-center justify-center w-6 flex-shrink-0" data-testid={testId ? `${testId}-icon` : undefined}>
          {status === 'error' ? (
            <CircleAlert stroke="white" fill="red" size={20} />
          ) : status === 'completed' ? (
            <Circle color="#009E82" fill="#009E82" size={8} />
          ) : status === 'current' ? (
            <div className="relative">
              <Circle color="#009E82" fill="#009E82" size={12} />
              <Circle 
                color="#009E82" 
                fill="#009E82" 
                size={12} 
                className="absolute inset-0 animate-ping opacity-75" 
              />
            </div>
          ) : (
            <Circle color="#A0A1AD" fill="#A0A1AD" size={10} />
          )}
        </div>

        {!isLastStep && (
          <div
            className="flex flex-col items-center mt-1 flex-1 w-full"
            data-testid={testId ? `${testId}-connector` : undefined}
          >
            {status === 'completed' ? (
              <div
                className="w-0.5 flex-1 bg-[length:2px_4px] bg-gradient-to-b from-[#009E82] from-50% to-transparent to-50% bg-repeat-y"
                style={{ backgroundSize: '2px 10px', minHeight: '80px' }}
              />
            ) : (
              <div
                className="w-0.5 flex-1 bg-[length:2px_4px] bg-gradient-to-b from-muted-foreground from-50% to-transparent to-50% bg-repeat-y"
                style={{ backgroundSize: '2px 10px', minHeight: '80px' }}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex-1 self-start" data-testid={testId ? `${testId}-content` : undefined}>
        {status === 'error' && (
          <p className="typography-body text-primary" data-testid={testId ? `${testId}-errorText` : undefined}>{errorText}</p>
        )}
        <p
          className={cn(
            'typography-body text-primary',
            (status === 'completed' || status === 'current') ? '!font-bold' : '!font-light text-tertiary',
            status === 'error' ? 'mt-2' : null,
          )}
          data-testid={testId ? `${testId}-text` : undefined}
        >
          {text}
        </p>
        {!isLastStep && status === 'current' && (
          <>
            {subHeaderExplanation && (
              <p
                className="typography-body text-secondary mt-5 mb-5"
                data-testid={testId ? `${testId}-subHeaderExplanation` : undefined}
              >
                {subHeaderExplanation}
              </p>
            )}
            <p className="typography-title text-tertiary mt-5 mb-5" data-testid={testId ? `${testId}-whatsNext` : undefined}>
              What's Next
            </p>
          </>
        )}
      </div>
    </div>
  );
};
